import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import useModal from '@shared/hooks/useModal';
import { notifyError } from '@config/tostifyConfig/tostifyConfig';
import { companiesService } from '@shared/services/companiesService';
import { art123Service } from '../services/art123Service';

const mapChangeEmployer = (items) =>
    (Array.isArray(items) ? items : []).map((item) => ({
        id: item.id,
        idn: item.idn,
        name: item.firstName,
        middleName: item.fatherName,
        surname: item.surName,
        businessNumber: item.empId,
        typeOfContract: item.typeContract,
        numberOfContract: item.contractNo,
        dateOfContract: item.contractDate,
        terminatedDate: item.breakDateContract,
    }));

const mapChangeEmployerContracts = (items) =>
    (Array.isArray(items) ? items : []).map((item) => ({
        id: item.id,
        enforcedFrom: item.effectiveDate,
        idn: item.idn,
        name: item.name,
        middleName: item.fatherName,
        surname: item.surName,
        businessNumber: item.empId,
        status: item.status,
        basis: item.reason,
    }));

const buildSearchParams = (searchQuery, selectedCompany) => {
    const params = {};
    const queries = typeof searchQuery === 'object' && searchQuery !== null ? searchQuery : {};

    if (queries.idn?.trim()) {
        params.idn = queries.idn.trim();
    }
    if (queries.name?.trim()) {
        params.name = queries.name.trim();
    }
    if (queries.fathersName?.trim()) {
        params.fathersName = queries.fathersName.trim();
    }
    if (queries.surname?.trim()) {
        params.surname = queries.surname.trim();
    }
    if (queries.empId?.trim()) {
        params.empId = queries.empId.trim();
    }
    if (selectedCompany) {
        params.adminBranchId = selectedCompany;
    }

    return params;
};

const formatDateForApi = (value) => {
    if (!value) return '';
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
        return value.slice(0, 10);
    }

    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return '';

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

export const useArt123 = () => {
    const { t } = useTranslation();
    const adminBranchId = useSelector((state) => state.auth.adminBranchId);
    const { modalState, handleOpenModal, handleCloseModal } = useModal();
    const [view, setView] = useState('employer');
    const [tableData, setTableData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchType, setSearchType] = useState('');
    const [selectedCompany, setSelectedCompany] = useState('');
    const [companyOptions, setCompanyOptions] = useState([]);
    const [generateResult, setGenerateResult] = useState(null);
    const [searchQuery, setSearchQuery] = useState({
        idn: '',
        name: '',
        fathersName: '',
        surname: '',
        empId: '',
    });

    const searchOptions = [
        { key: 'idn', label: 'idn' },
        { key: 'name', label: 'firstName' },
        { key: 'fathersName', label: 'fathersName' },
        { key: 'surname', label: 'surname' },
        { key: 'empId', label: 'number' },
    ];

    useEffect(() => {
        if (adminBranchId === null || adminBranchId === undefined) {
            setSelectedCompany('');
            setCompanyOptions([]);
        } else {
            setSelectedCompany(String(adminBranchId));
        }
    }, [adminBranchId]);

    useEffect(() => {
        const fetchCompanyName = async () => {
            if (adminBranchId === null || adminBranchId === undefined) return;

            try {
                const company = await companiesService.getSelectedCompany(adminBranchId);
                const name = company?.name || company?.branchName || '';
                setCompanyOptions([{ id: adminBranchId, name }]);
            } catch (e) {
                console.error('Error fetching company name:', e);
                setCompanyOptions([{ id: adminBranchId, name: String(adminBranchId) }]);
            }
        };

        fetchCompanyName();
    }, [adminBranchId]);

    const fetchData = useCallback(async (targetView = view) => {
        setView(targetView);
        setLoading(true);
        setError(null);

        const isEmployerView = targetView === 'employer';
        const fetchFn = isEmployerView
            ? art123Service.getEmployerChanges
            : art123Service.getContractsChanges;
        const mapper = isEmployerView ? mapChangeEmployer : mapChangeEmployerContracts;

        try {
            const data = await fetchFn(buildSearchParams(searchQuery, selectedCompany));
            const content = data?.content ?? data;
            setTableData(mapper(content));
        } catch (e) {
            console.error(`Error fetching art123 ${targetView} data:`, e);
            setError(e?.response?.data?.message || e?.message || String(e));
            setTableData([]);
        } finally {
            setLoading(false);
        }
    }, [searchQuery, selectedCompany, view]);

    useEffect(() => {
        fetchData('employer');
        // Initial load only
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSearch = useCallback(() => {
        fetchData();
    }, [fetchData]);

    const handleCloseGenerateResult = useCallback(() => {
        setGenerateResult(null);
    }, []);

    const handleGenerateAgreement = useCallback(async (formData) => {
        const payload = {
            contractDate: formatDateForApi(formData.documentDate),
            effectiveDate: formatDateForApi(formData.effectiveDate),
            reasonId: Number(formData.reasonId),
        };

        try {
            const response = await art123Service.generateAdditionalAgreement(payload);
            handleCloseModal();
            setGenerateResult(response);
            fetchData(view);
        } catch (error) {
            console.error('Error generating additional agreement:', error);
            notifyError(error?.response?.data?.message || error?.message || t('common.errorMessage'));
            throw error;
        }
    }, [fetchData, handleCloseModal, t, view]);

    return {
        t,
        view,
        tableData,
        loading,
        error,
        searchType,
        setSearchType,
        searchQuery,
        setSearchQuery,
        searchOptions,
        selectedCompany,
        setSelectedCompany,
        companyOptions,
        modalState,
        handleOpenModal,
        handleCloseModal,
        fetchData,
        handleSearch,
        handleGenerateAgreement,
        generateResult,
        handleCloseGenerateResult,
    };
};
